'use client';

import { useRouter } from '@bprogress/next';
import type { BreadcrumbItem } from '@ui/Breadcrumb';
import { isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useBikeFilter } from 'context/BikeFilterContext';
import { ArrowRightIcon, WrenchIcon, XCircleIcon } from 'lucide-react';
import type { UserBike } from 'models/shared';
import { useState } from 'react';
import DesktopSearchByVehicle from './SearchByVehicle/DesktopSearchByVehicle';
import MobileSearchByVehicle from './SearchByVehicle/MobileSearchByVehicle';
import { useWarehouse } from './WarehouseProvider';

export default function VehicleSpecificPrompt({
  description = 'Select a vehicle to see the part number and price.',
  showNoFit = true,
  breadcrumbs,
}: {
  description?: string;
  showNoFit?: boolean;
  breadcrumbs?: BreadcrumbItem[];
} = {}) {
  const { noFit } = useWarehouse();
  const { selectedBike, setSelectedBike, clearSelectedBike } = useBikeFilter();
  const { userDoc } = useAuth();
  const router = useRouter();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [showDesktopSearchByVehicle, setShowDesktopSearchByVehicle] =
    useState(false);

  const openModal = () => {
    if (isMobile()) {
      setShowMobileSearchByVehicle(true);
    } else {
      setShowDesktopSearchByVehicle(true);
    }
  };

  const handleBikeComplete = (bike: UserBike) => {
    if (bike.type && bike.make && bike.model && bike.year) {
      setSelectedBike(bike);
    }
  };

  const handleFindItems = () => {
    if (!selectedBike.make || !selectedBike.model || !selectedBike.year) return;
    const bikeParam = `?bikes=${selectedBike.year}:${selectedBike.make}:${selectedBike.model}`;
    router.push(
      `${breadcrumbs?.[1] ? breadcrumbs[1].href : '/shop/all'}${bikeParam}`
    );
  };

  return (
    <div className="flex flex-col gap-y-4">
      <div className="relative flex min-h-[8rem] w-full flex-col items-start gap-4 rounded-[1rem] border-2 border-gray-300 border-t-brand-secondary px-4 pb-8 pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-x-4">
        <div className="flex items-center gap-x-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yellow-400">
            <WrenchIcon className="h-6 w-6 text-white" />
          </div>
          <div className="flex flex-col gap-y-1">
            <span className="text-[1.6rem] font-bold text-black">
              This product is vehicle specific.
            </span>
            <span className="text-[1.6rem] text-black">{description}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={openModal}
          className="w-full whitespace-nowrap rounded-full bg-black px-8 py-5 text-[1.4rem] font-semibold uppercase tracking-wide text-white transition-colors hover:bg-gray-800 sm:w-auto sm:py-3"
        >
          {selectedBike.make ? 'Change vehicle' : 'Select your vehicle'}
        </button>
      </div>

      {showNoFit && noFit && (
        <div className="flex flex-col items-start gap-y-3 rounded-[1rem] bg-red-500/10 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-x-4">
            <XCircleIcon className="h-[2.5rem] w-[2.5rem] shrink-0 text-red-600" />
            <span className="text-[1.6rem] font-medium text-black">
              This product doesn&apos;t fit your {selectedBike.year}{' '}
              {selectedBike.make} {selectedBike.model}.
            </span>
          </div>
          <button
            type="button"
            onClick={handleFindItems}
            className="flex items-center gap-x-2 whitespace-nowrap text-[1.6rem] font-bold text-black underline"
          >
            Find parts that fit
            <ArrowRightIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      <MobileSearchByVehicle
        show={showMobileSearchByVehicle}
        dismiss={() => setShowMobileSearchByVehicle(false)}
        onBikeComplete={handleBikeComplete}
        onClear={clearSelectedBike}
        initialBike={selectedBike}
        userDoc={userDoc}
      />

      <DesktopSearchByVehicle
        isOpen={showDesktopSearchByVehicle}
        dismiss={() => setShowDesktopSearchByVehicle(false)}
        onBikeComplete={handleBikeComplete}
        onClear={clearSelectedBike}
        initialBike={selectedBike}
      />
    </div>
  );
}
